import React from 'react';
import styled from 'styled-components';
import {Helmet} from 'react-helmet';
import {Button, message, Tag, Tooltip, Select} from 'antd';
import {useOutlet, useOutletSetter} from 'reconnect.js';
import BreadcrumbBar from 'rev.sdk.js/Templates/ProductList/BreadcrumbBar';
import ProductVariants from '../../Components/ProductVariants';
import FilterMenu from 'rev.sdk.js/Templates/ProductList/FilterMenu';
import * as Cart from 'rev.sdk.js/Actions/Cart';
import * as JStorage from 'rev.sdk.js/Actions/JStorage';
import tracker from 'rev.sdk.js/Utils/Tracker';
import ProductExtraConfig from 'rev.sdk.js/Components/ProductExtraConfig';
import {Link as LinkIcon} from '@styled-icons/boxicons-regular/Link';
import {ArrowRight} from '@styled-icons/bootstrap/ArrowRight';
import Config from '../../../data.json';
import withPageEntry from '../../withPageEntry';
import {mapLineBreak} from '../../Utils/TextUtil';
import * as StockUtil from '../../Utils/StockUtil';
import {formatDateStr} from '../../Utils/TimeUtil';
import * as CustomBrowse from '../../Utils/CustomBrowse';
import * as AppActions from '../../AppActions';
import Carousel from '../../Components/Carousel';
import FixedRatioImage from '../../Components/FixedRatioImage';
import CustomProductDetailSectionD, {
  checkLogisticDisabled,
} from '../../Components/CustomProductDetailSectionD';
import qs from 'query-string';

// seasonbakery custom

const F = AppActions.F;
const L = AppActions.L;
const iconSize = 24;
const gutter = 12;
const THERMOSPHERE = {
  normal: '常溫',
  refrigeration: '冷藏',
  frozen: '冷凍',
};
const THERMOSPHERE_en = {
  normal: 'Ambient',
  refrigeration: 'Refrigerated',
  frozen: 'Frozen',
};

function getProductIdFromLocation(location) {
  const params = qs.parse(location.search);
  return params.id;
}
function canAddToCart(product) {
  const {isOnSale} = StockUtil.getOnSaleInfo(product);
  const isSession = product.session && Object.keys(product.session).length > 0;
  const isAvailableSession =
    isSession &&
    new Date(
      `${product.session.date} ${product.session.start_time}`,
    ).getTime() > new Date().getTime();
  return isOnSale && (!isSession || isAvailableSession);
}

function ProductDetail(props) {
  const {
    pageContext: {
      collection = 'product',
      prefixPath = '/product',
      listViewPath = '/products',
      outlets = {categoryDisplayMap: 'categoryDisplayMap'},
      prebuiltProduct = null,
      prebuiltArticle = null,
      features = {cat: true},
    },
  } = props;
  const id = getProductIdFromLocation(props.location);
  const [product, setProduct] = React.useState(prebuiltProduct);
  const [locale] = useOutlet('locale');

  const {
    filterMenuType,
    renderCustomSection,
  } = CustomBrowse.getExtraProductDetailPage(props);

  const [activeSummaryTab, setActiveSummaryTab] = React.useState('intro');
  const [currItemConfig, setCurrItemConfig] = React.useState({qty: 1});
  const [currPrice, setCurrPrice] = React.useState(null);
  const [currOriginalPrice, setCurrOriginalPrice] = React.useState(null);
  const [article, setArticle] = React.useState(prebuiltArticle);
  const [imgIdx, setImgIdx] = React.useState(0);
  const [mobileFilterVisible, setMobileFilterVisible] = React.useState(false);
  const [selectedDt, setSelectedDt] = React.useState(null);
  const [extraConfigs, setExtraConfigs] = React.useState({});
  const [nIn1Products, setNIn1Products] = React.useState([]);

  const [user] = useOutlet('user');
  const showLoginModal = useOutletSetter('login-modal');
  const [dimension] = useOutlet('dimension');
  const isPrebuilt = !!prebuiltProduct;
  const mobile = dimension.rwd === 'mobile';

  React.useEffect(() => {
    async function fetchData() {
      AppActions.setLoading(true);
      try {
        setProduct(
          await JStorage.fetchOneDocument('product', {id}, null, {
            anonymous: true,
          }),
        );
      } catch (ex) {
        console.warn('ProductDetail.useEffect ex', ex);
      }
      AppActions.setLoading(false);
    }

    if (!prebuiltProduct && id) {
      fetchData();
    }
  }, [id, prebuiltProduct]);

  React.useEffect(() => {
    async function fetchData() {
      try {
        const articleId = F(product, 'article');
        if (articleId) {
          setArticle(
            await JStorage.fetchOneDocument(
              'Article_Default',
              {
                id: articleId,
              },
              null,
              {anonymous: true},
            ),
          );
        }

        if (product.type === 'n_in_1') {
          setNIn1Products(
            await JStorage.fetchAllDocuments(
              'product',
              {
                _id: {
                  $in: [
                    ...(product.n_in_1_products || []).map((id) => ({
                      $oid: id,
                    })),
                  ],
                },
              },
              null,
              null,
              {anonymous: true},
            ),
          );
        }
      } catch (ex) {
        console.warn(ex);
      }
    }

    fetchData();
  }, [product, locale.lang]);

  React.useEffect(() => {
    function triggerTracker(product) {
      try {
        tracker.viewItem({
          name: 'product',
          item: product,
        });
      } catch (ex) {
        console.warn(ex);
      }
    }

    if (!isPrebuilt) {
      AppActions.setLoading(false);
      triggerTracker(product);
    } else {
      // waiting for tracker initial setup.
      setTimeout(() => triggerTracker(product), 2500);
    }
  }, [isPrebuilt, product]);

  const onNextConfig = React.useCallback((nextItemConfig, calcResp, extra) => {
    setCurrPrice(calcResp.amount);
    setCurrOriginalPrice(extra.original_price);
    setCurrItemConfig(nextItemConfig);
  }, []);

  async function copyShareUrl() {
    let _url = product.prebuilt_url
      ? `${Config.webHost}${product.prebuilt_url}`
      : AppActions.getReurl({
          title: encodeURIComponent(product.name),
          image: product.og_image || '',
          redirectUrl: `${Config.webHost}/product?id=${product.id}`,
        });

    try {
      await navigator.clipboard.writeText(_url);
      tracker.share({
        name: 'product',
        item: product,
        method: 'url',
      });
      message.success(`已複製分享連結`);
    } catch (err) {
      console.log(err);
      message.warn(`無法複製連結`);
    }
  }

  async function addToCart() {
    const logisticDisabledReason = checkLogisticDisabled({
      logistics_type: extraConfigs?.logistics_type,
      variants: product.variants,
      currConfig: currItemConfig,
    });
    if (logisticDisabledReason) {
      return message.error(
        `${F(logisticDisabledReason.variant, 'label')} ${F(
          logisticDisabledReason.choice,
          'label',
        )} ${L('not_support_logsitic')}`,
      );
    }

    try {
      AppActions.setLoading(true);

      await Cart.addToCart(product.id, {...currItemConfig, extraConfigs});
      tracker.addToCart({product, quantity: currItemConfig.qty});
      message.success(L('success'));
    } catch (ex) {
      console.warn(ex);
      if (ex?.response?.error === 'invalid_date') {
        message.error(L('cart_invalid_date'));
      } else if (ex?.response?.error === 'item_length_limit') {
        message.error(AppActions.L('cart_add_error_message_items_limit'));
      } else if (ex?.response?.error === 'invalid_extra_data') {
        message.error(L('cart_invalid_extra_data'));
      } else if (ex?.response?.error === 'date_mismatch') {
        message.error(L('cart_date_mismatch'));
      } else if (ex?.response?.error === 'logistics_mismatch') {
        message.error(L('cart_logistics_mismatch'));
      } else if (ex?.response?.error === 'thermosphere_mismatch') {
        message.error(L('cart_thermosphere_mismatch'));
      } else if (ex?.response?.error === 'config_mismatch') {
        message.error(L('cart_config_mismatch'));
      } else if (ex?.response?.error === 'invalid_qty') {
        message.error(L('cart_invalid_qty'));
      } else if (ex?.response?.error === 'exceed_limit') {
        message.error(L('cart_exceed_limit'));
      } else {
        message.error(AppActions.L('cart_add_error_message'));
      }
    } finally {
      AppActions.setLoading(false);
    }
  }

  if (!product) {
    return <div style={{minHeight: 'calc(100vh)'}} />;
  }

  let gallerySize = dimension.rwd === 'tablet' ? 338 : 600;
  if (dimension.innerWidth) {
    if (dimension.innerWidth < gallerySize + gutter * 2) {
      gallerySize = dimension.innerWidth - gutter * 2;
    }
  }

  const [saleStartDate, saleEndDate] = StockUtil.getStartEndDate(product);
  const {isOnSale, label: addToCartLabel} = StockUtil.getOnSaleInfo(product);
  const disabled =
    product.type === 'n_in_1'
      ? ![product, ...nIn1Products].every((p) => canAddToCart(p))
      : !canAddToCart(product);

  return (
    <Wrapper id="rev-ProductDetailPage">
      {product && (
        <Helmet>
          <meta charset="utf-8" />
          <meta property="og:title" content={product.name || ''} />
          <meta
            property="og:image"
            content={product.images ? product.images[0]?.expected_url : ''}
          />
          <meta property="og:image:width" content="300" />
          <meta property="og:image:height" content="300" />
          <meta name="title" content={product.title || ''} />
          <meta name="description" content={product.intro || ''} />
          <meta
            name="image"
            content={product.images ? product.images[0]?.expected_url : ''}
          />
        </Helmet>
      )}

      <div className="content">
        <LeftSection style={{maxWidth: gallerySize}}>
          <Gallery dimension={dimension}>
            {product.images && (
              <MiniImageList>
                {product.images.map((image, index) => (
                  <MiniImageItem
                    src={image.expected_url}
                    key={index}
                    alt="mini"
                    selected={index === imgIdx}
                    onClick={() => setImgIdx(index)}
                  />
                ))}
                <MiniImageItem css={{height: 0}} />
                <MiniImageItem css={{height: 0}} />
                <MiniImageItem css={{height: 0}} />
              </MiniImageList>
            )}
            {product.images && product.images[imgIdx] && (
              <Carousel
                pointCurrIdx={imgIdx}
                width={gallerySize}
                height={gallerySize}
                data={product.images.map((i) => i.expected_url)}
                renderPrev={null}
                renderNext={null}
                renderDots={null}
                renderItem={({item}) => {
                  return (
                    <FixedRatioImage
                      image={item}
                      width="100%"
                      ratio={1}
                      mode="cover"
                      alt="product"
                      // extraCss="border-radius: 15px;"
                    />
                  );
                }}
                disableSwipe
              />
            )}
          </Gallery>

          {article && (
            <ArticlePreview dangerouslySetInnerHTML={{__html: article.html}} />
          )}
        </LeftSection>

        <div style={{flex: '0 0 30px'}} />

        <RightSection>
          <h2>{F(product, 'name')}</h2>

          <div className="price">
            {(currOriginalPrice !== currPrice ||
              product.original_price > 0) && (
              <h3 className="text-line-through">
                NT${currOriginalPrice || product.original_price}
              </h3>
            )}
            <h3>NT${currPrice || '-'}</h3>
          </div>

          {product?.thermosphere && (
            <Tag style={{marginBottom: gutter, marginRight: gutter}}>
              宅配：
              {locale.lang === 'tw'
                ? THERMOSPHERE[product?.thermosphere]
                : THERMOSPHERE_en[product?.thermosphere]}
            </Tag>
          )}
          {product?.thermosphere_self_pick && (
            <Tag style={{marginBottom: gutter}}>
              自取：
              {locale.lang === 'tw'
                ? THERMOSPHERE[product?.thermosphere_self_pick]
                : THERMOSPHERE_en[product?.thermosphere_self_pick]}
            </Tag>
          )}

          <div
            className="intro"
            dangerouslySetInnerHTML={{
              __html: mapLineBreak(F(product, 'intro')),
            }}
          />

          <div className="variants">
            <ProductVariants product={product} onNextConfig={onNextConfig} />

            <CustomProductDetailSectionD
              product={product}
              values={extraConfigs}
              setValues={setExtraConfigs}
              currItemConfig={currItemConfig}
              onNextConfig={onNextConfig}
            />

            <ProductExtraConfig
              product={product}
              values={extraConfigs}
              setValues={setExtraConfigs}
            />
          </div>

          <div
            style={{display: 'flex', justifyContent: 'space-between'}}
            className="buttons">
            <button
              onClick={async () => {
                if (!user) {
                  showLoginModal(true);
                  return;
                }

                await addToCart();
                AppActions.navigate('/checkout', {loading: true});
              }}
              disabled={disabled}
              className="buy-now">
              {AppActions.L('buy_now')}
            </button>
            <div style={{width: gutter}} />

            <button
              onClick={async () => {
                if (!user) {
                  showLoginModal(true);
                  return;
                }

                await addToCart();
              }}
              disabled={disabled}
              className="add-to-cart">
              {addToCartLabel}
            </button>
          </div>

          <Tooltip title={AppActions.L('share_link')}>
            <button className="share-btn" onClick={copyShareUrl}>
              <svg width={iconSize} height={iconSize}>
                <use xlinkHref="/images/icon-share.svg#_圖層_2" />
              </svg>
            </button>
          </Tooltip>
        </RightSection>
      </div>
    </Wrapper>
  );
}

const Wrapper = styled.div`
  margin-bottom: 100px;
  padding-top: var(--topNavBarHeight);

  & > .content {
    max-width: var(--contentMaxWidth);
    margin: 0 auto;
    display: flex;
    padding: 50px 0;

    @media screen and (max-width: 1200px) {
      padding: 30px 50px;
    }
    @media screen and (max-width: 768px) {
      padding: 30px 20px;
    }

    & > .categories {
      padding-top: 12px;
      display: flex;
      flex-direction: column;

      & > h3 {
        border-bottom: 1px solid #ccc;
        padding: 4px 8px;
        align-self: center;
      }
    }

    & h2 {
      font-weight: 300;
      color: var(--text-5-color);
    }
  }

  @media (max-width: 768px) {
    & > .content {
      flex-direction: column-reverse;

      & > .categories {
        & > ul {
          width: 100% !important;
        }
      }
    }
  }
`;

const Gallery = styled.div`
  width: ${(props) => props.size}px;
  margin-bottom: ${gutter}px;
  display: flex;

  & > img:first-child {
    width: ${(props) => props.size}px;
    height: ${(props) => props.size}px;
    object-fit: contain;
  }
`;

const MiniImageList = styled.div`
  display: flex;
  flex-wrap: wrap;
  flex-direction: column;
  margin-right: ${gutter}px;

  @media (max-width: 500px) {
    /* justify-content: space-around; */
  }
`;

const MiniImageItem = styled.img`
  width: 72px;
  height: 72px;
  object-fit: contain;
  margin-bottom: ${gutter}px;
  cursor: pointer;

  :first-child,
  :nth-child(5n + 1) {
    margin-left: 0px;
  }

  :last-child,
  :nth-child(5n) {
    margin-right: 0px;
  }

  :hover,
  :active {
    /* box-shadow: 0 3px 6px 0 rgba(0, 0, 0, 0.16); */
  }

  @media (max-width: 500px) {
    width: 62px;
    height: 62px;
  }

  @media (max-width: 430px) {
    margin: 10px 4px;
    width: 58px;
    height: 58px;
  }

  ${(props) => props.css || ''}
`;

const RightSection = styled.div`
  flex: 1;
  // width: 100%;

  & > .price h3 {
    margin-bottom: 40px;
    font-weight: 300;
    color: var(--text-5-color);
  }

  & .variants {
    margin-bottom: 25px;
    & > div {
      align-items: flex-start;
      & > div > label {
        margin-bottom: 9px;
        font-weight: 300;
        color: var(--text-3-color);
        &:first-letter {
          text-transform: uppercase;
        }
      }
    }

    & .ant-radio-group.ant-radio-group-solid {
      justify-content: flex-start;
      & > .radio-btn {
        margin-right: 10px;
      }
    }

    & .product-quantity > div {
      border: solid 1px var(--input-border-color);
      border-radius: 0;
      & > input {
        background-color: var(--background-color);
        border: none;
      }
      & > .minus-container {
        border: none;
        border-right: solid 1px var(--input-border-color);
        background-color: var(--background-color);
        color: var(--input-border-color);
      }
      & > .plus-container {
        border: none;
        border-left: solid 1px var(--input-border-color);
        background-color: var(--background-color);
        color: var(--input-border-color);
      }
    }
  }

  & .intro {
    font-weight: 300;
    color: var(--text-3-color);
    margin-bottom: 40px;
  }

  & .buttons {
    margin-bottom: ${gutter}px;

    & > .add-to-cart,
    .buy-now {
      border: solid 1px var(--btn-border-color);
      flex: 1;
      color: var(--text-5-color);
      padding: 8px;
      background-color: transparent;
      font-size: 16px;
      cursor: pointer;
    }
    & > .add-to-cart {
      color: var(--text-reverse-color);
      background-color: var(--background-reverse-color);
    }
  }

  & button.share-btn {
    color: var(--icon-color);
    display: flex;
    align-items: center;
    justify-content: center;
    border: 1px solid var(--btn-border-color);
    border-radius: 0;
    width: 40px;
    height: 40px;
    background-color: transparent;
    cursor: pointer;

    &:hover {
      background-color: transparent;
    }
  }
`;

const LeftSection = styled.div``;

const ArticlePreview = styled.div`
  margin: 0 auto;
  max-width: 980px;
  width: 100%;
  padding-left: calc(72px + ${gutter}px);
  white-space: pre-wrap;
  font-weight: 300;
  color: var(--text-3-color);

  & img {
    max-width: 100%;
  }
`;

const MobileMenuBtn = styled.div`
  position: fixed;
  top: 84px;
  right: 0px;
`;

const MobileFilter = styled.div`
  z-index: 99;
  position: fixed;
  background-color: white;
  top: 0;
  left: 0;
  opacity: ${(props) => (props.visible ? 1 : 0)};
  overflow: auto;
  height: 100vh;
  transform: ${(props) =>
    props.visible ? 'translateX(0px)' : 'translateX(-300px)'};
  transition: 180ms;
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.19), 0 6px 6px rgba(0, 0, 0, 0.23);
`;

const Backdrop = styled.div`
  z-index: 99;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  transition: 180ms;
  opacity: ${(props) => (props.visible ? 1 : 0)};
  background-color: rgba(0, 0, 0, 0.5);
  pointer-events: ${(props) => (props.visible ? 'auto' : 'none')};
`;

export default withPageEntry(ProductDetail);
